/**
 * CompareShortResolver — resolves a `/c/:code` URL to its canonical
 * `/compare/unis?ids=…` target and navigates the browser there.
 * Renders a lightweight loading state while the resolve request is
 * in flight; on 404 or malformed code, shows a graceful "link not
 * found" screen with a route back to /explore.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, XCircle } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

export default function CompareShortResolver() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get(`/compare/resolve/${encodeURIComponent(code)}`)
      .then((r) => {
        if (!alive) return;
        trackEvent("compare_short_resolved", { code });
        navigate(`/compare/unis?ids=${encodeURIComponent(r.data.ids)}`, {
          replace: true,
        });
      })
      .catch((e) => alive && setErr(e?.response?.data?.detail || "not found"));
    return () => { alive = false; };
  }, [code, navigate]);

  if (err) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="compare-short-error">
        <XCircle className="h-10 w-10 mx-auto text-rose-500" />
        <h1 className="mt-4 font-editorial text-3xl text-[#002147]">
          Short link not found
        </h1>
        <p className="mt-2 text-stone-600">
          The compare link <code>/c/{code}</code> couldn't be resolved.
          It may be malformed or an older link that was never generated.
        </p>
        <Link
          to="/compare/unis"
          data-testid="compare-short-error-back"
          className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[#4C1D95] text-white px-4 py-2 text-sm font-underground-sc tracking-wider hover:bg-[#3a1476]"
        >
          Build a new compare set
        </Link>
      </div>
    );
  }

  return (
    <div
      className="max-w-lg mx-auto px-6 py-24 text-center text-stone-500 flex flex-col items-center gap-3"
      data-testid="compare-short-loading"
    >
      <Loader2 className="h-6 w-6 animate-spin text-[#4C1D95]" />
      <div>Resolving compare link…</div>
    </div>
  );
}
